<script setup>
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useVueFlow,
  MarkerType,
} from "@vue-flow/core";
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  sourceX: {
    type: Number,
    required: true,
  },
  sourceY: {
    type: Number,
    required: true,
  },
  targetX: {
    type: Number,
    required: true,
  },
  targetY: {
    type: Number,
    required: true,
  },
  sourcePosition: {
    type: String,
    required: true,
  },
  targetPosition: {
    type: String,
    required: true,
  },
  data: {
    type: Object,
    required: false,
  },
  markerEnd: {
    type: String,
    required: false,
    default: MarkerType.ArrowClosed,
  },
  cssStyle: {
    type: Object,
    required: false,
  },
});

const labelText = computed(() => {
  if (props.data.label === null) return null;
  return props.data.label;
});
const { removeEdges, findNode, updateNode } = useVueFlow();

const path = computed(() => getBezierPath(props));

function removeEdge(id) {
  //Split the edge id into the source id and target id based on "-->"
  const ids = id.split("-->");

  //Finds the target node by id and removes source from its inputs
  let targetNode = findNode(ids[1]);
  if (targetNode) {
    const filteredInputs = (targetNode.userData.inputs || []).filter((input) => {
      return typeof input === "object" ? input.id !== ids[0] : input !== ids[0];
    });
    updateNode(ids[1], {
      userData: { ...targetNode.userData, inputs: filteredInputs },
    });
  }

  //Finds the source node by id and removes target from its outputs
  let sourceNode = findNode(ids[0]);
  if (sourceNode) {
    const filteredOutputs = (sourceNode.userData.outputs || []).filter((output) => {
      return typeof output === "object" ? output.id !== ids[1] : output !== ids[1];
    });

    // If the source node is an ifelse, also clear the True/False path parameter pointing to the removed target
    if (sourceNode.userData.idprefix === "ifelse") {
      const updatedParams = (sourceNode.userData.parameters || []).map((param) => {
        if (param.property === ids[1]) {
          return { ...param, property: "" };
        }
        return param;
      });
      updateNode(ids[0], {
        userData: { ...sourceNode.userData, outputs: filteredOutputs, parameters: updatedParams },
      });
    } else {
      updateNode(ids[0], {
        userData: { ...sourceNode.userData, outputs: filteredOutputs },
      });
    }
  }

  //Removes the edge
  removeEdges(id);
}

// Crea una variabile reattiva per tracciare se l'edge è stato selezionato
const edgeSelected = ref(false);

// Aggiungi un gestore di eventi per il clic sull'edge
function onEdgeClick(event) {
  event.stopPropagation();
  edgeSelected.value = !edgeSelected.value;
}

// Aggiungi un gestore di eventi globale per il clic sulla pagina
function onPageClick() {
  edgeSelected.value = false;
}

onMounted(() => {
  window.addEventListener("click", onPageClick);
});

onUnmounted(() => {
  window.removeEventListener("click", onPageClick);
});
</script>

<template>
  <g @click="onEdgeClick">
    <defs>
      <marker
        id="arrowEnd"
        markerWidth="10"
        markerHeight="10"
        refX="7"
        refY="5"
        orient="auto-start-reverse"
        markerUnits="strokeWidth"
      >
        <path d="M0,0 L0,10 L10,5 z" fill="#C8004B" />
      </marker>
    </defs>
    <BaseEdge
      :id="id"
      :style="{ ...cssStyle, stroke: edgeSelected ? '#003050' : 'grey' }"
      :path="path[0]"
      :markerEnd="'url(#arrowEnd)'"
    />
    <EdgeLabelRenderer>
      <div
        v-if="labelText !== null"
        :style="{
          pointerEvents: 'auto',
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${path[1] - 40}px,${path[2] - 0}px)`,
        }"
        class="nodrag nopan"
        :class="[labelText ? 'labelStyle' : '']"
      >
        {{ labelText?.toUpperCase() }}
      </div>
      <div
        :style="{
          pointerEvents: 'auto',
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${path[1]}px,${path[2]}px)`,
        }"
        class="nodrag nopan"
      >
        <v-tooltip text="Cut the link">
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="edgeSelected"
              v-bind="props"
              density="compact"
              icon
              color="white"
              class="mx-1 on-hover"
              size="small"
              @click="removeEdge(id)"
              variant="tonal"
            >
              <v-icon
                icon="mdi-content-cut"
                color="secondary"
                size="x-small"
              ></v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </div>
    </EdgeLabelRenderer>
  </g>
</template>

<style scoped>
.labelStyle {
  background-color: white;
  border: 1px solid lightgrey;
  padding: 0 2px;
  border-radius: 2px;
  font-size: 0.8em;
}
</style>
